import { useState, useEffect } from 'react';
import { MsB2b } from '@api/ms-b2b';

// Mirrors ENTITY_MAP in b2b-api/app/controllers/comments.js — comment entity_type to the
// notification target.entity string, needed to match unseen "mentioned" notifications back
// to the specific record this button is attached to.
const ENTITY_TARGET_MAP = {
  sales_order: 'orders',
  return_order: 'return-orders',
  skp_internal: 'skp-internals',
  skp_external: 'skp-externals',
  request_product: 'request-product-tester',
  request_product_eligibility: 'request-product-eligibilities',
  lpb: 'lpb',
  customer: 'customers',
};

// Shared between FloatCommentButton (web) and CommentHeaderButton (mobile) — same unread
// dot behavior, just rendered in different places per platform.
export default function useCommentUnread({
  entityType,
  entityId,
  hasUnread,
  drawerVisible,
}) {
  const [autoUnread, setAutoUnread] = useState(false);

  // Force-clear whenever the drawer isn't open — covers both the click-to-open moment and the
  // close transition, so the dot can't reappear from a stray re-fetch/remount in between.
  useEffect(() => {
    if (!drawerVisible) {
      setAutoUnread(false);
    }
  }, [drawerVisible]);

  useEffect(() => {
    if (hasUnread !== undefined || !entityType || !entityId) {
      return;
    }
    let cancelled = false;
    // Filter by verb only server-side — `seen` is a boolean field, and query-string params
    // round-trip as strings ("false"), which can silently fail to match a real boolean in
    // MongoDB. Checking seen/target client-side avoids betting on that cast happening correctly.
    MsB2b.getNotifications({ params: { filter: { verb: 'mentioned' } } })
      .then((response) => {
        if (cancelled) {
          return;
        }
        const targetEntity = ENTITY_TARGET_MAP[entityType];
        const notifications = response?.data?.data || [];
        setAutoUnread(
          notifications.some(
            (n) =>
              !n.seen &&
              n.target?.entity === targetEntity &&
              String(n.target?.id) === String(entityId),
          ),
        );
      })
      .catch(() => setAutoUnread(false));
    return () => {
      cancelled = true;
    };
  }, [entityType, entityId, hasUnread]);

  const showUnread = hasUnread !== undefined ? hasUnread : autoUnread;

  const clear = () => setAutoUnread(false);

  return [showUnread, clear];
}
